import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { ContentCopy, Share } from "@mui/icons-material";
import { successAlert, errorAlert } from "../../utils/swalConfig";
import api from "../../utils/api";

const MyReferrals = () => {
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState({
    referralCode: "",
    referralLink: "",
    totalReferrals: 0,
    referralEarnings: 0,
    referrals: [],
  });

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("jwt");
      const response = await api.get("/users/referrals", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        setReferralData(response.data.data);
      }
    } catch (error) {
      // console.error("Referral API Error:", error.response || error); // Debug log
      errorAlert("Error", "Failed to fetch referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    successAlert("Copied!", "Copied to clipboard");
  };

  const shareReferralLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join AtkLand",
          text: "Join me on AtkLand and let's build a sustainable future together! Use my referral code for exclusive benefits.",
          url: referralData.referralLink,
        });
      } catch (err) {
        // console.error("Error sharing:", err);
      }
    } else {
      copyToClipboard(referralData.referralLink);
    }
  };

  const ReferralCodeSkeleton = () => (
    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
      <Skeleton variant="rectangular" width="100%" height={56} />
      <Skeleton variant="circular" width={40} height={40} />
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Referral Code Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Referral Code
              </Typography>
              {loading ? (
                <>
                  <ReferralCodeSkeleton />
                  <Typography variant="h6" gutterBottom>
                    Referral Link
                  </Typography>
                  <ReferralCodeSkeleton />
                </>
              ) : (
                <>
                  <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                    <TextField
                      fullWidth
                      value={referralData.referralCode}
                      InputProps={{ readOnly: true }}
                    />
                    <IconButton
                      onClick={() => copyToClipboard(referralData.referralCode)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    Referral Link
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      fullWidth
                      value={referralData.referralLink}
                      InputProps={{ readOnly: true }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<Share />}
                      onClick={shareReferralLink}
                    >
                      Share
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Referral Stats Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Referral Statistics
              </Typography>
              {loading ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Referrals
                    </Typography>
                    <Skeleton variant="rectangular" height={42} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Earnings
                    </Typography>
                    <Skeleton variant="rectangular" height={42} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Referrals
                    </Typography>
                    <Typography variant="h4">
                      {referralData.totalReferrals}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Earnings
                    </Typography>
                    <Typography variant="h4">
                      ₦{referralData.referralEarnings.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Referral List Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Referrals
              </Typography>
              {loading ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date Joined</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Earnings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[1, 2, 3].map((i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : referralData.referrals.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date Joined</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Earnings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {referralData.referrals.map((referral) => (
                        <TableRow key={referral._id}>
                          <TableCell>{referral.name}</TableCell>
                          <TableCell>
                            {new Date(referral.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {referral.hasDeposited ? "Active" : "Pending"}
                          </TableCell>
                          <TableCell>
                            ₦{referral.totalEarnings?.toLocaleString() || "0"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                  You haven't referred anyone yet. Share your referral link to
                  start earning!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyReferrals;
